/**
 * Save Progress Drawer Component
 * Bottom drawer for confirming save of reading progress when leaving reader
 */

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { BookmarkCheck, Loader2 } from 'lucide-react';

interface SaveProgressDrawerProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  currentPage: number;
  startPage: number;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
  isSaving?: boolean;
}

const MADANI_MAX_PAGE = 604;


export function SaveProgressDrawer({
  open,
  onOpenChange,
  currentPage,
  startPage,
  onSave,
  onDiscard,
  onCancel,
  isSaving = false
}: SaveProgressDrawerProps) {
  const handleOpenChange = (next: boolean) => {
    if (!next) onCancel();
    onOpenChange?.(next);
  };

  // Calculate pages read
  // Logic: if current >= start, simple diff. If wrap around (e.g. start=600, current=2), wrap calculation.
  // Note: startPage from ReadPage is "last saved page". So we start reading from startPage + 1.
  // Actual pages read = (current) - (startPage).
  // Example: Last saved 592. Read 593..604..2.
  // Count: (604 - 592) + 2 = 12 + 2 = 14 pages.
  // Formula:
  // If current > start: current - start
  // If current <= start: (604 - start) + current
  
  const pagesRead = currentPage >= startPage
    ? currentPage - startPage
    : (MADANI_MAX_PAGE - startPage) + currentPage;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl px-4 py-6">
        <SheetHeader className="mb-6 text-left">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <BookmarkCheck className="w-6 h-6 text-primary" />
            บันทึกความคืบหน้า
          </SheetTitle>
          <SheetDescription className="text-base">
             คุณต้องการบันทึกการอ่านรอบนี้หรือไม่?
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6">
           {/* Session Impact Card */}
           <div className="rounded-xl border border-border bg-muted/30 p-5 text-center">
            <p className="text-sm font-medium text-muted-foreground mb-1">ผลการอ่านรอบนี้</p>
            <p className="text-5xl font-bold text-primary mb-2">
              +{pagesRead} <span className="text-lg font-normal text-foreground">หน้า</span>
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground bg-background/50 rounded-lg py-1 px-3 inline-flex">
              <span>จากหน้า {startPage}</span>
              <span>→</span>
              <span className="font-semibold text-foreground">ถึงหน้า {currentPage}</span>
            </div>
          </div>
        </div>

        <SheetFooter className="flex-col gap-3 mt-8 sm:flex-col">
          <Button
            onClick={onSave}
            className="w-full gap-2 text-base h-12"
            size="lg"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                กำลังบันทึก...
              </>
            ) : (
              'บันทึกและออก'
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={onDiscard}
            className="w-full gap-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-10"
            disabled={isSaving}
          >
            ออกโดยไม่บันทึก
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default SaveProgressDrawer;
